import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PlusCircle, KeyRound, Trash2, Users, LogIn, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, errMsg } from "../../lib/api";
import { useAuth, DASHBOARD_HOME } from "../../context/AuthContext";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, ConfirmDialog } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY_FORM = { name: "", phone: "", email: "", password: "", confirm: "", plan: "starter" };

export default function RootAdmins() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showCreate, setShowCreate] = useState(false);
  const [creds, setCreds] = useState(null);
  const [edit, setEdit] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [partners, setPartners] = useState([]);
  const { impersonate } = useAuth();
  const navigate = useNavigate();

  const access = async (a) => {
    try {
      const r = await api.post("/root/impersonate", { user_id: a.id });
      impersonate(r.data);
      toast.success(`Now viewing as ${r.data.user.name}`);
      navigate(DASHBOARD_HOME[r.data.user.role] || "/");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => { api.get("/root/admins").then((r) => setData(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <LoadingRows rows={4} />;

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    try {
      const r = await api.post("/root/admins", { name: form.name, phone: form.phone, email: form.email, password: form.password, plan: form.plan });
      if (r.data.temporary_password) setCreds(r.data);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      toast.success("Super Admin created — they can log in with their email and password");
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  const patch = async (id, updates) => {
    try { await api.patch(`/root/admins/${id}`, updates); toast.success("Updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveEdit = async () => {
    if (!edit.name.trim() || !edit.email.trim()) { toast.error("Name and email are required"); return; }
    await patch(edit.id, { name: edit.name, phone: edit.phone, email: edit.email });
    setEdit(null);
  };
  const reset = async (id) => {
    try { const r = await api.post(`/root/admins/${id}/reset`); setCreds(r.data); toast.success("Access reset — new password generated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const viewPartners = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    const r = await api.get(`/root/admins/${id}/partners`);
    setPartners(r.data); setExpanded(id);
  };

  return (
    <div data-testid="root-admins">
      <PageHeader title="Super Admins" subtitle="Create, plan-assign and manage the platform's super admin accounts.">
        <Button data-testid="create-admin-open" onClick={() => setShowCreate(!showCreate)} className="rounded-full font-bold">
          <PlusCircle className="h-4 w-4 mr-2" /> New Super Admin
        </Button>
      </PageHeader>

      {creds && (
        <div data-testid="admin-creds" className="border border-emerald-500/30 bg-emerald-500/5 rounded-md p-5 mb-6 font-mono text-sm space-y-1 max-w-lg">
          <p>Email: <span className="font-bold">{creds.email || creds.admin?.email}</span></p>
          <p>Password: <span className="font-bold">{creds.password || creds.temporary_password}</span></p>
          <p className="text-xs text-muted-foreground font-sans">Shown once — share securely.</p>
        </div>
      )}

      {showCreate && (
        <form onSubmit={create} className="bg-card border border-border rounded-md p-6 mb-8 max-w-5xl" data-testid="create-admin-form">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label className="overline-label">Full Name</Label>
              <Input data-testid="admin-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Phone Number</Label>
              <Input data-testid="admin-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Email Address</Label>
              <Input data-testid="admin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Password</Label>
              <Input data-testid="admin-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Confirm Password</Label>
              <Input data-testid="admin-confirm-password" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Plan</Label>
              <select data-testid="admin-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {data.plans.map((p) => <option key={p.key} value={p.key}>{p.name} — {p.max_partners} partners · {fmtINR(p.price)}/mo</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-5">
            <Button data-testid="create-admin-submit" type="submit" className="rounded-full font-bold">Create Super Admin</Button>
            <p className="text-xs text-muted-foreground">The password is stored securely hashed and is never shown again after creation.</p>
          </div>
        </form>
      )}

      <TableShell testid="admins-table" head={["Super Admin", "Plan", "Partners", "Websites", "Status", "Actions"]}>
        {data.admins.map((a) => (
          <TR key={a.id} testid={`admin-row-${a.id}`}>
            <TD><div><p className="font-bold text-sm">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p></div></TD>
            <TD>
              <select data-testid={`admin-plan-select-${a.id}`} value={a.plan || "enterprise"} onChange={(e) => patch(a.id, { plan: e.target.value })}
                className="h-9 rounded-md border border-input bg-background px-2 text-xs font-bold">
                {data.plans.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">{a.plan_detail?.max_partners} partners · {fmtINR(a.plan_detail?.price || 0)}/mo</p>
            </TD>
            <TD>
              <button data-testid={`admin-partners-${a.id}`} onClick={() => viewPartners(a.id)} className="font-bold text-sm hover:underline flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />{a.partners_count}
              </button>
            </TD>
            <TD className="font-bold">{a.websites_count}</TD>
            <TD><StatusBadge status={a.status} /></TD>
            <TD>
              <div className="flex gap-1.5">
                <button data-testid={`admin-edit-${a.id}`} onClick={() => setEdit({ id: a.id, name: a.name, phone: a.phone || "", email: a.email })} title="Edit details"
                  className="h-8 w-8 rounded-full border border-border hover:bg-muted flex items-center justify-center transition-colors">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`admin-access-${a.id}`} onClick={() => access(a)} title="Access Account"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-extrabold gradient-bg text-white hover:opacity-85 transition-opacity">
                  <LogIn className="h-3.5 w-3.5" /> Access
                </button>
                <button data-testid={`admin-toggle-${a.id}`} onClick={() => patch(a.id, { status: a.status === "active" ? "suspended" : "active" })}
                  className="px-3 py-1.5 rounded-full text-xs font-bold border border-border hover:bg-muted transition-colors">
                  {a.status === "active" ? "Suspend" : "Activate"}
                </button>
                <button data-testid={`admin-reset-${a.id}`} onClick={() => reset(a.id)} title="Reset access"
                  className="h-8 w-8 rounded-full border border-border hover:bg-muted flex items-center justify-center transition-colors">
                  <KeyRound className="h-3.5 w-3.5" />
                </button>
                <ConfirmDialog testid={`admin-delete-${a.id}`} title="Delete super admin?"
                  description="Their partners and websites keep working — only this admin account is removed."
                  onConfirm={async () => { await api.delete(`/root/admins/${a.id}`); toast.success("Super admin deleted"); load(); }}
                  trigger={<button className="h-8 w-8 rounded-full border border-border hover:bg-red-500/10 text-red-500 flex items-center justify-center transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>} />
              </div>
            </TD>
          </TR>
        ))}
      </TableShell>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit Super Admin</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2" data-testid="edit-admin-form">
            <div className="space-y-1.5"><Label className="overline-label">Full Name</Label>
              <Input data-testid="edit-admin-name" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Phone Number</Label>
              <Input data-testid="edit-admin-phone" value={edit?.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline-label">Email Address</Label>
              <Input data-testid="edit-admin-email" type="email" value={edit?.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></div>
            <Button data-testid="edit-admin-save" onClick={saveEdit} className="w-full rounded-full font-bold">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {expanded && (
        <div className="mt-6" data-testid="admin-partners-panel">
          <p className="overline-label mb-3">Partners under this super admin — billed independently per partner</p>
          {partners.length === 0 ? <p className="text-sm text-muted-foreground">No partners yet.</p> : (
            <TableShell testid="admin-partners-table" head={["Partner", "Email", "Active Websites", "Monthly Billing", "Subscription"]}>
              {partners.map((p) => (
                <TR key={p.id} testid={`ap-row-${p.id}`}>
                  <TD className="font-bold text-sm">{p.name}</TD>
                  <TD className="text-sm">{p.email}</TD>
                  <TD className="font-bold">{p.active_websites}</TD>
                  <TD className="font-bold">{fmtINR(p.monthly_amount)}</TD>
                  <TD><StatusBadge status={p.subscription_status || p.status} /></TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}
    </div>
  );
}
